import React from 'react';
import { Row, Col, Form, FormGroup, Label, Input } from 'reactstrap';
import { formatNumber } from '../../../../utils/formatUtils';
import apiService from 'service/apiService';

function RequestedAmountStep({ value, onChange, errors = {}, totalSubscribers, onTotalSubscribersChange, totalMonthlyRevenue, onTotalMonthlyRevenueChange, monitoringCenter, onMonitoringCenterChange, monitoringCenterId, onMonitoringCenterIdChange, mode = 'APPLICATION' }) {
	const [monitoringCenterOptions, setMonitoringCenterOptions] = React.useState([]);

	// Load monitoring center options
	React.useEffect(() => {
		const loadMonitoringCenters = async () => {
			try {
				const data = await apiService.getMonitoringCentres();
				// Handle both array response and paginated response
				const monitoringCenters = Array.isArray(data) ? data : (data?.content || []);
				const options = monitoringCenters.map(mc => {
					// Handle the new structure with id and legal_name
					if (typeof mc === 'object' && mc !== null) {
						const id = mc.id;
						const name = mc.legal_name || mc['legal-name'] || mc.legalName || mc.name;
						return {
							value: name,
							label: name,
							id: id
						};
					}
					// Fallback for old string format
					return {
						value: mc,
						label: mc,
						id: null
					};
				});
			setMonitoringCenterOptions(options);
			
			// Set default monitoring center if available and not already set
			// Only set default in new APPLICATION mode, not when editing (APPLICATION_INCOMPLETE)
			if (options.length > 0 && !monitoringCenter && mode !== 'APPLICATION_INCOMPLETE') {
				onMonitoringCenterChange({ target: { value: options[0].value } });
				if (onMonitoringCenterIdChange) {
					onMonitoringCenterIdChange(options[0].id);
				}
			}
			} catch (error) {
				console.error('Error loading monitoring centers:', error);
			}
		};

		loadMonitoringCenters();
	}, []); // Solo ejecutar una vez al montar el componente
	const handleAmount = (e) => {
		let v = e.target.value || '';
		// Solo dígitos, sin puntos ni decimales
		v = v.replace(/\D/g, '');
		// Limitar a 10 dígitos
		if (v.length > 10) v = v.slice(0, 10);
		onChange(v);
	};

	const handleSubscribers = (e) => {
		let v = e.target.value || '';
		// Solo dígitos
		v = v.replace(/\D/g, '');
		// Limitar a 10 dígitos
		if (v.length > 10) v = v.slice(0, 10);
		onTotalSubscribersChange(v);
	};

	const handleRevenue = (e) => {
		let v = e.target.value || '';
		// Permitir dígitos y un punto decimal
		v = v.replace(/[^\d.]/g, '');
		// Solo un punto decimal
		const parts = v.split('.');
		if (parts.length > 2) {
			v = parts[0] + '.' + parts.slice(1).join('');
		}
		// Limitar a 2 decimales
		if (parts[1] && parts[1].length > 2) {
			v = parts[0] + '.' + parts[1].slice(0, 2);
		}
		// Limitar a 15 dígitos totales
		if (v.replace('.', '').length > 15) {
			const withoutDot = v.replace('.', '');
			const truncated = withoutDot.slice(0, 15);
			if (v.includes('.')) {
				const dotIndex = v.indexOf('.');
				v = truncated.slice(0, dotIndex) + '.' + truncated.slice(dotIndex);
			} else {
				v = truncated;
			}
		}
		onTotalMonthlyRevenueChange(v);
	};

	return (
		<Form onSubmit={(e) => e.preventDefault()}>
			<Row>
				<Col md="3">
					<FormGroup className="mb-2">
						<Label htmlFor="requestedAmount" className="mb-1">Requested Amount <span>*</span></Label>
						<Input 
							id="requestedAmount" 
							name="requestedAmount" 
							type="text" 
							inputMode="numeric" 
							placeholder="0" 
							value={value} 
							onChange={handleAmount} 
							maxLength={10}
							required 
							className={errors.requestedAmount ? 'is-invalid' : ''} 
							bsSize="sm"
						/>
						{errors.requestedAmount && <div className="invalid-feedback d-block">{errors.requestedAmount}</div>}
					</FormGroup>
				</Col>
				<Col md="3">
					<FormGroup className="mb-2">
						<Label htmlFor="totalSubscribers" className="mb-1">Total Subscribers <span>*</span></Label>
						<Input 
							id="totalSubscribers" 
							name="totalSubscribers" 
							type="text" 
							inputMode="numeric" 
							placeholder="0" 
							value={totalSubscribers} 
							onChange={handleSubscribers} 
							maxLength={10}
							required 
							className={errors.totalSubscribers ? 'is-invalid' : ''} 
							bsSize="sm"
						/>
						{errors.totalSubscribers && <div className="invalid-feedback d-block">{errors.totalSubscribers}</div>}
					</FormGroup>
				</Col>
				<Col md="3">
					<FormGroup className="mb-2">
						<Label htmlFor="totalMonthlyRevenue" className="mb-1">Total Monthly Revenue <span>*</span></Label>
						<Input 
							id="totalMonthlyRevenue" 
							name="totalMonthlyRevenue" 
							type="text" 
							inputMode="decimal" 
							placeholder="0.00" 
							value={totalMonthlyRevenue} 
							onChange={handleRevenue} 
							maxLength={18}
							required 
							className={errors.totalMonthlyRevenue ? 'is-invalid' : ''} 
							bsSize="sm"
						/>
						{errors.totalMonthlyRevenue && <div className="invalid-feedback d-block">{errors.totalMonthlyRevenue}</div>}
					</FormGroup>
				</Col>
				<Col md="3">
					<FormGroup className="mb-2">
						<Label htmlFor="monitoringCenter" className="mb-1">Monitoring Center <span>*</span></Label>
						<Input 
							id="monitoringCenter" 
							name="monitoringCenter" 
							type="select" 
							value={monitoringCenter} 
							onChange={(e) => {
								const selectedValue = e.target.value;
								onMonitoringCenterChange(e);
								// Find the corresponding ID for the selected name
								const selectedOption = monitoringCenterOptions.find(option => option.value === selectedValue);
								if (onMonitoringCenterIdChange) {
									onMonitoringCenterIdChange(selectedOption ? selectedOption.id : null);
								}
							}}
							required 
							className={errors.monitoringCenter ? 'is-invalid' : ''} 
							bsSize="sm"
						>
							<option value="">Select Monitoring Center</option>
							{monitoringCenterOptions.map(option => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</Input>
						{errors.monitoringCenter && <div className="invalid-feedback d-block">{errors.monitoringCenter}</div>}
					</FormGroup>
				</Col>
			</Row>

			<hr></hr>
		</Form>
	);
}

export default RequestedAmountStep;




